import React, { useEffect, useState, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  ProgressBar,
} from "react-bootstrap";

import {
  FiUsers,
  FiBriefcase,
  FiCheckCircle,
  FiTrendingUp,
  FiCalendar,
  FiDollarSign,
  FiActivity,
  FiEye,
} from "react-icons/fi";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
} from "recharts";

import "./AdminDashboard.css";

const AdminDashboard = () => {
  // =========================================
  // STATIC KPI DATA
  // =========================================

  const [stats, setStats] = useState({
    candidates: 1245,
    castingDirectors: 48,
    projects: 126,
    roles: 876,
    auditions: 4521,
    revenue: 128500,
    activeUsers: 982,
    applications: 6543,
  });

  // =========================================
  // CHART DATA
  // =========================================

  const monthlyData = [
    { month: "Jan", auditions: 120, users: 90, revenue: 12000 },
    { month: "Feb", auditions: 180, users: 110, revenue: 18000 },
    { month: "Mar", auditions: 260, users: 160, revenue: 24000 },
    { month: "Apr", auditions: 320, users: 210, revenue: 30000 },
    { month: "May", auditions: 420, users: 280, revenue: 38000 },
    { month: "Jun", auditions: 520, users: 350, revenue: 46000 },
  ];

  const roleCategoryData = [
    { name: "TV Ads", value: 35 },
    { name: "Web Series", value: 25 },
    { name: "Movies", value: 20 },
    { name: "Reality Shows", value: 10 },
    { name: "Music Videos", value: 10 },
  ];

  const auditionData = [
    { name: "Mon", auditions: 12 },
    { name: "Tue", auditions: 18 },
    { name: "Wed", auditions: 22 },
    { name: "Thu", auditions: 15 },
    { name: "Fri", auditions: 30 },
    { name: "Sat", auditions: 44 },
    { name: "Sun", auditions: 20 },
  ];

  const COLORS = [
    "#ff1744",
    "#ff5252",
    "#ff8a80",
    "#d50000",
    "#ff6e6e",
  ];

  // =========================================
  // KPI CARDS
  // =========================================

  const statCards = useMemo(
    () => [
      {
        title: "Candidates",
        value: stats.candidates,
        icon: <FiUsers />,
        growth: "+12%",
      },

      {
        title: "Casting Directors",
        value: stats.castingDirectors,
        icon: <FiUsers />,
        growth: "+5%",
      },

      {
        title: "Projects",
        value: stats.projects,
        icon: <FiBriefcase />,
        growth: "+18%",
      },

      {
        title: "Roles",
        value: stats.roles,
        icon: <FiCheckCircle />,
        growth: "+9%",
      },

      {
        title: "Auditions",
        value: stats.auditions,
        icon: <FiCalendar />,
        growth: "+23%",
      },

      {
        title: "Revenue",
        value: `₹${stats.revenue.toLocaleString()}`,
        icon: <FiDollarSign />,
        growth: "+31%",
      },

      {
        title: "Applications",
        value: stats.applications,
        icon: <FiActivity />,
        growth: "+14%",
      },

      {
        title: "Active Users",
        value: stats.activeUsers,
        icon: <FiEye />,
        growth: "+7%",
      },
    ],
    [stats]
  );

  // =========================================
  // RECENT PROJECTS
  // =========================================

  const recentProjects = [
    {
      id: 1,
      name: "CID Season 2",
      type: "Web Series",
      status: "Active",
      applications: 456,
    },

    {
      id: 2,
      name: "Big Cola Ad",
      type: "Advertisement",
      status: "Casting",
      applications: 198,
    },

    {
      id: 3,
      name: "Dance India",
      type: "Reality Show",
      status: "Completed",
      applications: 721,
    },

    {
      id: 4,
      name: "Action Hero",
      type: "Movie",
      status: "Active",
      applications: 852,
    },
  ];

  // =========================================
  // RECENT AUDITIONS
  // =========================================

  const auditions = [
    {
      candidate: "Rahul Sharma",
      role: "Lead Hero",
      project: "CID Season 2",
      status: "Shortlisted",
    },

    {
      candidate: "Anjali Mehta",
      role: "Supporting Actress",
      project: "Action Hero",
      status: "Pending",
    },

    {
      candidate: "Karan Singh",
      role: "Villain",
      project: "CID Season 2",
      status: "Selected",
    },

    {
      candidate: "Riya Verma",
      role: "Model",
      project: "Big Cola Ad",
      status: "Rejected",
    },
  ];

  // =========================================
  // RENDER
  // =========================================

  return (
    <div className="admin-dashboard-page">
      {/* ========================================= */}
      {/* PAGE HEADER */}
      {/* ========================================= */}

      <div className="dashboard-header mb-4">
        <h2 className="dashboard-title">
          Dashboard Overview
        </h2>

        <p className="dashboard-subtitle">
          Welcome back Admin 👋
        </p>
      </div>

      {/* ========================================= */}
      {/* KPI CARDS */}
      {/* ========================================= */}

      <Row className="g-4 mb-4">
        {statCards.map((card, index) => (
          <Col
            xs={12}
            sm={6}
            lg={3}
            key={index}
          >
            <Card className="dashboard-stat-card h-100">
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div className="dashboard-icon">
                    {card.icon}
                  </div>

                  <div className="ms-3">
                    <p className="dashboard-card-title">
                      {card.title}
                    </p>

                    <h3 className="dashboard-card-value">
                      {card.value}
                    </h3>
                  </div>
                </div>

                <Badge bg="danger">
                  {card.growth}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ========================================= */}
      {/* CHART SECTION */}
      {/* ========================================= */}

      <Row className="g-4 mb-4">
        {/* AREA CHART */}
        <Col lg={8}>
          <Card className="dashboard-chart-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="chart-title">
                  Monthly Platform Growth
                </h5>

                <Badge bg="danger">
                  2026
                </Badge>
              </div>

              <ResponsiveContainer
                width="100%"
                height={320}
              >
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient
                      id="colorRevenue"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#ff1744"
                        stopOpacity={0.8}
                      />

                      <stop
                        offset="95%"
                        stopColor="#ff1744"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#222"
                  />

                  <XAxis dataKey="month" />

                  <YAxis />

                  <Tooltip />

                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#ff1744"
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* PIE CHART */}
        <Col lg={4}>
          <Card className="dashboard-chart-card h-100">
            <Card.Body>
              <h5 className="chart-title mb-4">
                Project Categories
              </h5>

              <ResponsiveContainer
                width="100%"
                height={320}
              >
                <PieChart>
                  <Pie
                    data={roleCategoryData}
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    fill="#8884d8"
                    dataKey="value"
                    label
                  >
                    {roleCategoryData.map(
                      (entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            COLORS[
                              index %
                                COLORS.length
                            ]
                          }
                        />
                      )
                    )}
                  </Pie>

                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ========================================= */}
      {/* BAR + LINE CHART */}
      {/* ========================================= */}

      <Row className="g-4 mb-4">
        {/* BAR */}
        <Col lg={6}>
          <Card className="dashboard-chart-card h-100">
            <Card.Body>
              <h5 className="chart-title mb-4">
                Weekly Auditions
              </h5>

              <ResponsiveContainer
                width="100%"
                height={300}
              >
                <BarChart data={auditionData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#222"
                  />

                  <XAxis dataKey="name" />

                  <YAxis />

                  <Tooltip />

                  <Legend />

                  <Bar
                    dataKey="auditions"
                    fill="#ff1744"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* LINE */}
        <Col lg={6}>
          <Card className="dashboard-chart-card h-100">
            <Card.Body>
              <h5 className="chart-title mb-4">
                User Activity
              </h5>

              <ResponsiveContainer
                width="100%"
                height={300}
              >
                <LineChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#222"
                  />

                  <XAxis dataKey="month" />

                  <YAxis />

                  <Tooltip />

                  <Legend />

                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#ff1744"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ========================================= */}
      {/* TABLES */}
      {/* ========================================= */}

      <Row className="g-4">
        {/* RECENT PROJECTS */}
        <Col lg={7}>
          <Card className="dashboard-table-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="chart-title">
                  Recent Projects
                </h5>

                <Badge bg="danger">
                  Live
                </Badge>
              </div>

              <div className="table-responsive">
                <Table
                  hover
                  borderless
                  className="dashboard-table align-middle"
                >
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Applications</th>
                    </tr>
                  </thead>

                  <tbody>
                    {recentProjects.map(
                      (project) => (
                        <tr key={project.id}>
                          <td>
                            {project.name}
                          </td>

                          <td>
                            {project.type}
                          </td>

                          <td>
                            <Badge
                              bg={
                                project.status ===
                                "Active"
                                  ? "success"
                                  : project.status ===
                                    "Casting"
                                  ? "warning"
                                  : "secondary"
                              }
                            >
                              {project.status}
                            </Badge>
                          </td>

                          <td>
                            {
                              project.applications
                            }
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* AUDITIONS */}
        <Col lg={5}>
          <Card className="dashboard-table-card h-100">
            <Card.Body>
              <h5 className="chart-title mb-4">
                Recent Auditions
              </h5>

              {auditions.map(
                (item, index) => (
                  <div
                    key={index}
                    className="audition-item"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6>
                          {item.candidate}
                        </h6>

                        <p className="mb-1 text-muted">
                          {item.role} •{" "}
                          {item.project}
                        </p>
                      </div>

                      <Badge
                        bg={
                          item.status ===
                          "Selected"
                            ? "success"
                            : item.status ===
                              "Shortlisted"
                            ? "warning"
                            : item.status ===
                              "Rejected"
                            ? "danger"
                            : "secondary"
                        }
                      >
                        {item.status}
                      </Badge>
                    </div>

                    <ProgressBar
                      now={
                        item.status ===
                        "Selected"
                          ? 100
                          : item.status ===
                            "Shortlisted"
                          ? 70
                          : item.status ===
                            "Pending"
                          ? 40
                          : 20
                      }
                      variant="danger"
                      className="mt-2"
                    />
                  </div>
                )
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;